var _excluded = ["variant", "type", "collapsible", "defaultValue", "onValueChange", "value", "startOpen"],
    _excluded2 = ["children", "value", "disabled"],
    _excluded3 = ["children", "wrap"],
    _excluded4 = ["children"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { useId } from '@radix-ui/react-id';
import { styled, forwardRef } from '@grupoboticario/flora-react-system';
import { Box } from '@grupoboticario/flora-react-box';
import { motion } from 'framer-motion';
import AccordionContext, { useAccordion } from './AccordionContext';
import AccordionItemContext, { useAccordionItem, useGetItem } from './AccordionItemContext';
import * as DOM from './lib/dom';
var ACCORDION_KEYS = ['Home', 'End', 'ArrowDown', 'ArrowUp'];
var StyledItem = styled('li', {
  '&:last-child': {
    marginBottom: 1
  },
  '&.keyboard': {
    '&.focused': {
      position: 'relative',
      zIndex: 1,
      '&:before': {
        content: '',
        display: 'block',
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 1,
        backgroundColor: 'transparent',
        pointerEvents: 'none',
        outline: 'none',
        top: 1,
        left: 0,
        borderRadius: 4,
        border: '2px solid $keyboardFocusDefault'
      }
    }
  }
});
var StyledHeader = styled('h3', {
  margin: 0,
  padding: 0,
  '&[data-level="3"]': {
    marginLeft: '-24px'
  }
});
var TRIGGER_DEFAULT_TAG = 'button';
var StyledTrigger = styled(TRIGGER_DEFAULT_TAG, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  textAlign: 'left',
  width: '100%',
  border: 'none',
  background: 'none',
  padding: '14px 0',
  minHeight: 48,
  cursor: 'pointer',
  fontSize: '$bodyLarge',
  fontWeight: '$regular',
  color: '$nonInteractivePredominant',
  outline: 'none',
  '& > span:first-of-type': {
    display: 'inline-flex',
    alignItems: 'center'
  },
  '&[data-level="1"]': {
    borderTop: '2px solid $colors$nonInteractiveOutline'
  },
  '*:not([data-level="1"]) > &': {
    color: '$nonInteractivePredominant'
  },
  '&[data-level="2"], &[data-level="3"]': {
    color: '$nonInteractiveAuxiliar'
  },
  '@hover': {
    '&:hover': {
      '[data-level="1"] > &': {
        '&:hover': {
          borderTop: '2px solid $colors$actionableDefault'
        }
      }
    }
  },
  '&:focus:not([data-state="open"]):not(:hover)': {
    '[data-level="1"] > &:focus, [data-level="1"] > &[data-state]': {
      borderTop: '2px solid $colors$actionableDefault'
    }
  },
  '[data-accordion-arrow]': {
    minWidth: '24px',
    display: 'inline-flex',
    justifyContent: 'center',
    '&[data-level="2"]': {
      minWidth: '20px'
    },
    '&[data-level="3"]': {
      minWidth: '16px'
    },
    '& > svg': {
      transition: 'transform 300ms cubic-bezier(0.65, 0, 0.35, 1)',
      fill: '$linkDefault',
      '[data-level="2"] &': {
        width: '10px'
      },
      '[data-level="3"] &': {
        width: '8px'
      }
    }
  },
  '&[data-state="open"]': {
    '[data-accordion-arrow] > svg': {
      transform: 'rotate(180deg)'
    }
  },
  '[data-level="2"] > &': {
    minHeight: 'auto',
    height: '44px',
    paddingLeft: '$6'
  },
  '[data-level="3"] > &': {
    minHeight: 'auto',
    height: '40px',
    paddingLeft: '$12',
    fontSize: '$bodySmall',
    lineHeight: '16px'
  }
});
var StyledPanel = styled(motion.ul, {
  padding: '0 0 0 0',
  color: '$nonInteractivePredominant',
  // NOSONAR
  fontSize: '$bodyLarge',
  lineHeight: '$taller',
  '[data-panel-content="true"]': {
    paddingBottom: '$3',
    paddingRight: '$1'
  },
  '[data-accordion-item]:not([data-level="1"]):nth-last-child(2) [data-panel-content="true"]': {
    paddingBottom: '$1'
  },
  '&[data-level="1"] [data-panel-content]': {
    paddingLeft: 0
  },
  '&[data-level="2"] [data-panel-content]': {
    paddingLeft: '$12'
  },
  '&[data-level="3"] [data-panel-content]': {
    paddingLeft: '$18'
  }
});
var StyledAccordion = styled('ul', {
  width: '100%',
  variants: {
    variant: {
      alt: {
        $$color: '$colors$nonInteractiveAltPredominant',
        ["& " + StyledTrigger]: {
          color: '$colors$nonInteractiveAltPredominant',
          '&[data-level="1"]': {
            borderTop: '2px solid $colors$nonInteractiveAltOutline'
          },
          '&[data-level="2"]': {
            color: '$colors$nonInteractiveAltAuxiliar'
          },
          '&[data-level="3"]': {
            color: '$colors$nonInteractiveAltAuxiliar'
          },
          '[data-accordion-arrow] > svg': {
            fill: '$linkAlt'
          },
          '&:not([data-level="1"]) > &': {
            color: '$nonInteractiveAltAuxiliar' // NOSONAR

          }
        },
        ["& " + StyledPanel]: {
          color: '$nonInteractiveAltAuxiliar' // NOSONAR

        },
        ["& " + StyledItem]: {
          '&.keyboard': {
            '&.focused': {
              '&:before': {
                border: '2px solid $keyboardFocusAlt'
              }
            }
          }
        }
      }
    }
  }
});

/**
 * @component
 * @name Accordion
 * @description Accordion component
 * @tutorial https://flora.gb.tech/docs/components/core/accordion
 */
export var Accordion = forwardRef((_ref, forwardedRef) => {
  var {
    variant,
    type,
    collapsible = false,
    defaultValue = '',
    onValueChange,
    value = '',
    startOpen = false
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  useEffect(() => {
    if (!['single', 'multiple'].includes(type || '')) {
      throw new Error("Missing prop `type` expected on Accordion");
    }
  }, [type]);
  return /*#__PURE__*/React.createElement(AccordionContext.Provider, {
    value: _extends({}, rest, {
      defaultValue,
      value,
      variant: variant,
      collapsible,
      type,
      startOpen,
      onValueChange
    })
  }, /*#__PURE__*/React.createElement(StyledAccordion, _extends({
    onKeyUp: event => {
      var parent = DOM.getParentByAttribute(document.activeElement, DOM.DATA_ACCORDION_ITEM);
      parent == null ? void 0 : parent.classList.add('keyboard');
    },
    "data-accordion": true,
    variant: variant
  }, rest, {
    ref: forwardedRef
  })));
});
export var AccordionItem = forwardRef((_ref2, forwardedRef) => {
  var {
    children,
    value = '',
    disabled = false
  } = _ref2,
      props = _objectWithoutPropertiesLoose(_ref2, _excluded2);

  var {
    addItem,
    addChild
  } = useAccordion();
  var {
    state,
    level,
    value: currentAccordionValue
  } = useGetItem(value);
  var triggerId = useId();
  var labelledId = useId();
  var [alreadyAdded, setAlreadyAdded] = useState(false);
  var ref = useRef(forwardedRef);
  var canUseDOM = typeof window !== 'undefined';
  var useIsomorphicLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    var _DOM$getParentByAttri;

    var parentAccordionValue = (_DOM$getParentByAttri = DOM.getParentByAttribute(ref.current, DOM.DATA_ACCORDION_ITEM)) == null ? void 0 : _DOM$getParentByAttri.getAttribute('data-value');
    var currentLevel = DOM.getAccordionLevel(ref.current);
    /* istanbul ignore else */

    if (value && triggerId && !currentAccordionValue && !level && !alreadyAdded) {
      setAlreadyAdded(true);
      addItem({
        value,
        triggerId,
        labelledId,
        level: currentLevel,
        parentValue: parentAccordionValue,
        disabled
      });
    }

    if (value && parentAccordionValue && currentAccordionValue) {
      addChild({
        value: parentAccordionValue,
        childValue: value
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [value, triggerId, currentAccordionValue, level, ref == null ? void 0 : ref.current, alreadyAdded]);
  return /*#__PURE__*/React.createElement(AccordionItemContext.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement(StyledItem, _extends({}, props, {
    "data-state": state,
    "data-accordion-item": true,
    "data-value": value,
    "data-level": level,
    ref: ref,
    onKeyDown: event => {
      var _target$parentElement, _keys$event$key;

      event.stopPropagation();
      var target = event.target;
      var isAccordionKey = ACCORDION_KEYS.includes(event.key);

      if (!isAccordionKey || !(target instanceof HTMLButtonElement || !((_target$parentElement = target.parentElement) != null && _target$parentElement.getAttribute('data-accordion-item')) || !target.getAttribute('data-accordion-item') || !target.getAttribute('aria-controls') || !target.getAttribute('data-control'))) {
        return;
      }

      var keys = {
        Home: () => DOM.goToHome(event.currentTarget),
        End: () => DOM.goToEnd(event.currentTarget),
        ArrowDown: () => DOM.goToNextItem(target, event.currentTarget),
        ArrowUp: () => DOM.goToPreviousItem(target, event.currentTarget)
      };
      (_keys$event$key = keys[event.key]) == null ? void 0 : _keys$event$key.call(keys);
    }
  }), children));
});
export var AccordionPanel = forwardRef((_ref3, forwardedRef) => {
  var {
    children,
    wrap = true
  } = _ref3,
      props = _objectWithoutPropertiesLoose(_ref3, _excluded3);

  var {
    state,
    triggerId = '',
    labelledId = '',
    level,
    childrenValue,
    isOpen
  } = useAccordionItem();
  var groupedChildren = React.Children.toArray(children).reduce((acc, child, index) => {
    var lastIndex = acc.length - 1;
    var lastItem = acc.length > 0 ? acc[lastIndex] : null;

    if (lastItem && typeof lastItem === 'string') {
      acc[lastIndex] += ' ' + child;
    } else {
      acc[index] = child;
    }

    return acc;
  }, []);
  var animateProps = {
    animate: {
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? [0, 1] : [1, 0]
    },
    initial: {
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 0 : 1
    },
    transition: {
      height: {
        ease: [0.65, 0, 0.35, 1],
        duration: 0.3
      },
      opacity: {
        ease: isOpen ? 'easeIn' : 'easeOut',
        duration: isOpen ? 0.18 : 0.3
      }
    }
  };
  return /*#__PURE__*/React.createElement(Box, _extends({
    as: motion.div,
    role: "region",
    "aria-labelledby": labelledId,
    id: triggerId,
    css: {
      overflow: isOpen ? 'visible' : 'hidden'
    }
  }, animateProps), /*#__PURE__*/React.createElement(StyledPanel, _extends({
    "data-state": state,
    "data-accordion-panel": true,
    "data-has-children": !!(childrenValue != null && childrenValue.length),
    "data-level": level
  }, props, {
    ref: forwardedRef,
    role: "list"
  }), wrap && React.Children.map(groupedChildren, child => child.type !== AccordionItem ? /*#__PURE__*/React.createElement("li", {
    "data-panel-content": true
  }, /*#__PURE__*/React.createElement(React.Fragment, null, child)) : /*#__PURE__*/React.createElement(React.Fragment, null, child)), !wrap && children));
});
export var AccordionHeader = StyledHeader;
export var AccordionTrigger = forwardRef((_ref4, forwardedRef) => {
  var {
    children
  } = _ref4,
      props = _objectWithoutPropertiesLoose(_ref4, _excluded4);

  var {
    value,
    state,
    triggerId,
    isOpen,
    labelledId,
    level = 1,
    childrenValue,
    disabled
  } = useAccordionItem();
  var {
    toggleItem,
    toggleSingleItem,
    type
  } = useAccordion();
  var triggerRef = React.useRef(forwardedRef);

  var handleClick = () => {
    /* istanbul ignore else */
    if (type === 'multiple' || level > 1) {
      toggleItem({
        value
      });
    }
    /* istanbul ignore else */


    if (type === 'single' && level === 1) {
      toggleSingleItem({
        value
      });
    }
  };

  useEffect(() => {
    var triggerElement = triggerRef.current;

    var handleFocusIn = event => {
      var parent = DOM.getParentByAttribute(event.target, DOM.DATA_ACCORDION_ITEM);
      parent.classList.add('focused');
    };

    var handleFocusOut = event => {
      var parent = DOM.getParentByAttribute(event.target, DOM.DATA_ACCORDION_ITEM);
      parent.classList.remove('focused');
      parent.classList.remove('keyboard');
    };

    triggerElement.addEventListener('focusin', handleFocusIn);
    triggerElement.addEventListener('focusout', handleFocusOut);
    return () => {
      triggerElement.removeEventListener('focusin', handleFocusIn);
      triggerElement.removeEventListener('focusout', handleFocusOut);
    };
  }, []);
  return /*#__PURE__*/React.createElement(StyledTrigger, _extends({
    "data-state": state,
    "data-flora": "accordion",
    "data-level": level,
    "aria-expanded": isOpen,
    id: labelledId,
    type: "button",
    "aria-controls": triggerId,
    "data-has-children": !!(childrenValue != null && childrenValue.length),
    onClick: !disabled ? handleClick : () => ''
  }, props, {
    ref: triggerRef,
    "data-control": true
  }), /*#__PURE__*/React.createElement("span", null, children), /*#__PURE__*/React.createElement("span", {
    "data-accordion-arrow": true
  }, /*#__PURE__*/React.createElement("svg", {
    width: "12",
    height: "8",
    viewBox: "0 0 12 8",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M1.41.59L6 5.17 10.59.59 12 2 6 8 0 2 1.41.59z"
  }))));
});
export var AccordionButton = AccordionTrigger;
//# sourceMappingURL=Accordion.js.map